// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

// about vue-router
// MIT License
//
// Copyright (c) 2013-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { mapActions, useStore } from 'vuex';
import { ref, computed, getCurrentInstance, toRefs, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave, } from 'vue-router';

import fullModal from "../../../../share/components/vue/FullModal";
import { ICON_KEY, BUTTON_TYPE, default as noTextSvgButton } from "../../../../share/components/vue/NoTextSVGButton";
import wrappedTeleport from "../../../../share/components/vue/WrappedTeleport";
import { WrappedUseI18n } from '../../../../share/plugins/I18nWrapper/index';
import customProgress from "../../../../share/components/vue/CustomProgress";
import dataMonitor from '../../../vue/dataMonitor/DataMonitor';
import dmUtil, { DATA_STORAGE, TAB } from "../../../pages/dataMonitor/dataMonitorUtil";
import { createZoomTabHandlersForRefs, isResetZoomDisabled } from '../../../pages/dataMonitor/dataMonitorZoomUtil';
import { CsvFileWriter, getMeasurementFileName } from '../../../pages/dataMonitor/dataMonitorFileUtil';
import { exportCsv } from './csv';

export default {
  components: {
    dataMonitor,
    fullModal,
    noTextSvgButton,
    wrappedTeleport,
    customProgress
  },

  props: {
    isActiveTest: Boolean,
    isOnlyCsvExport: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  setup(props, context) {
    const currentComponent = getCurrentInstance();

    // 他ファイルで$t, $teを使えるように追加
    const { t, te } = WrappedUseI18n();
    currentComponent.$t = t;
    currentComponent.$te = te;

    // 他ファイルで$modalDialogを使えるように追加 ※share/alert.jsのshowXXX()を使用するために使用
    currentComponent.$modalDialog = currentComponent.appContext.config.globalProperties.$modalDialog;

    // 子コンポーネントの参照
    const data_monitor = ref(null);
    // 親コンポーネントからのデータ
    const { isActiveTest, isOnlyCsvExport } = toRefs(props);
    // リアクティブな変数
    const inPlaying = ref(false);
    const checkLines = ref(0);  // グラフ項目件数
    const currentTab = ref(TAB.LIST);
    const isZoomPanActive = ref(false);
    const highlightItems = ref([]);
    const currentPercentage = ref(0);
    const exportPercentage = ref(0);
    const dataMonitorFunctionSettings = new dmUtil.DataMonitorFunctionSettings(dmUtil.TYPE_CALLER.PLAY); // DM機能の設定インスタンス
    const recollectionAtInfo = ref([]);
    const atOperatingStatusList = ref([]);
    const isPhase6AtRecollection = ref(false);
    // 全画面モーダルの表示有無
    const showModal = ref(false);
    const showSelect = ref(false);
    const showCsvProgress = ref(false);
    // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
    const measureMounted = ref(false);
    // VuexのStoreの値・特定の算出の値
    const store = useStore();
    const cachedDetails = computed(() => store.state.cache.projectDetails).value;
    const btnDisabled = computed(() => !checkLines.value || isZoomPanActive.value); // ボタン使用不可制御
    const resetZoomBtnDisabled = computed(() => isResetZoomDisabled({
      inPlaying: inPlaying.value,
      hasCheckLines: Boolean(checkLines.value),
      currentTab: currentTab.value,
    }));
    // エクスポート可能かを管理するフラグ
    const ableToExport = ref(false);
    // 描画に使用せずコンポーネント内で保持するのみの値
    let measurementId = '';
    let storage = '';

    /**
     * データモニタの再生画面を表示するためのデータを取得する
     * @param {object} selectedMeasureData 測定時に選択されていた項目
     * @param {string} selectEcuName 再生対象のECU名
     */
    async function createDmtMeasuresList(selectedMeasureData, selectEcuName) {
      measurementId = selectedMeasureData.measurement_id;
      storage = selectedMeasureData.storage || DATA_STORAGE.INTDASH;
      // HEV系AT対応：Phase6対象HEV系AT
      isPhase6AtRecollection.value = dmUtil.isPhase6At(
        selectedMeasureData.item_name?.split(".")[1]?.substring(0, 3)
      );
      // HEV系AT対応：AT駆動情報を設定
      const modifiedData = JSON.parse(JSON.stringify(selectedMeasureData));
      // ツリーデータからの選択時についても初期化
      atOperatingStatusList.value = [];
      if (modifiedData.at_operating_status_list.length === 0) {
        atOperatingStatusList.value.push({ startTime: 0, stopTime: 0 });
      } else {
        modifiedData.at_operating_status_list.sort((a, b) => a.elapsed_time - b.elapsed_time);
        let i = 0;
        if (modifiedData.at_operating_status_list[0].operating_status === "stop") {
          // 駆動状態で計測が始まっている場合は、開始時刻から停止までの要素を追加
          atOperatingStatusList.value.push({
            startTime: 0,
            stopTime: modifiedData.at_operating_status_list[0].elapsed_time
          });
          i++;
        }
        for (i; i < modifiedData.at_operating_status_list.length; i += 2) {
          atOperatingStatusList.value.push({
            startTime: modifiedData.at_operating_status_list[i]?.elapsed_time ?? undefined,
            stopTime: modifiedData.at_operating_status_list[i + 1]?.elapsed_time ?? Infinity
          });
        }
      }
      // HEV系AT対応：IO/Routine Control対象を追加
      recollectionAtInfo.value = modifiedData.at_info;
      for (const item of Object.keys(modifiedData.monitor_items)) {
        let minimumDisplayOrder = 0;
        for (const element of modifiedData.monitor_items[item]) {
          minimumDisplayOrder = element.display_order <= minimumDisplayOrder ? element.display_order - 1 : minimumDisplayOrder;
        }
        for (const element of recollectionAtInfo.value) {
          modifiedData.monitor_items[item].push({
            display_order: minimumDisplayOrder,
            highlight: true,
            id: element.id
          });
        }
      }

      const currentMeasurementUuid = data_monitor.value.measurementUuid;
      const newMeasurementUuid = modifiedData.measurement_id;

      if (currentMeasurementUuid === newMeasurementUuid) {
        // 言語変更時にデータの再取得を実施しない
        return;
      }

      data_monitor.value.startPlayApi(
        modifiedData,
        selectEcuName,
        cachedDetails.information.id,
        recollectionAtInfo.value,
        isOnlyCsvExport.value ? ableToCsvExport : () => { }
      );
      highlightItems.value = Object.keys(modifiedData.monitor_items).map(systemName => {
        return modifiedData.monitor_items[systemName].filter(monitorItem => monitorItem.highlight).map(monitorItem => dmUtil.setSystem(systemName, monitorItem.id));
      }).flat();
      // 計測データ取得と同時に、モーダル表示
      if (!isOnlyCsvExport.value) {
        showModal.value = true;
      }
    }

    // グラフ設定
    function handleConfigureGraph() {
      data_monitor.value.handleConfigureGraph();
    }

    /**
     * メモボタン押下
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async function parentOpenMemoModal() {
      context.emit('openMemoModal');
    }

    /** 計測開始/停止状態切替 */
    function togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        inPlaying.value = !inPlaying.value;
      } else {
        inPlaying.value = flg;
      }
      if (inPlaying.value) {
        isZoomPanActive.value = false;
      }
    }

    /** グラフ項目件数を変更 */
    function changeCheckValue(value) {
      checkLines.value = value;
    }

    /**
     * ズーム/パン状態を更新
     * @param {boolean} isActive
     */
    function zoomPanStateChanged(isActive) {
      isZoomPanActive.value = isActive;
    }

    /**
     * 戻るボタン押下時
     * DataRecollection.jsのbackToDetailsを呼び出している
     */
    function backSelect() {
      context.emit('backToDetails');
    }

    /**
     * 取得したデータポイントの更新時の処理。プログレスバーの取得率を更新する。
     * @param {number} gotTotalCount 取得済みのデータポイント数
     */
    function updateTotalCount(gotTotalCount) {
      if (!data_monitor.value || !inPlaying.value) {
        return;
      }
      const allCount = data_monitor.value.receivedDataPoints;
      currentPercentage.value = dmUtil.calculatePercentage(allCount, gotTotalCount);
      // 親コンポーネントのプログレスバー更新
      // データポイントの進捗とCSVエクスポートの進捗の両方を受け取る必要があるため、データポイントの進捗率をCSVエクスポートの進捗率の半分として更新する
      context.emit('updateExportPercentage', currentPercentage.value / 2);
    }

    /**
     * CSVエクスポートの進捗更新
     * @param {number} allCount 全体数
     * @param {number} currentCount 現在の数
     */
    function updateExportProgress(allCount, currentCount) {
      exportPercentage.value = dmUtil.calculatePercentage(allCount, currentCount);
      // 親コンポーネントのプログレスバー更新
      // データポイントの進捗とCSVエクスポートの進捗の両方を受け取る必要があるため、CSVエクスポートの進捗率をデータポイントの進捗率の半分と合わせて更新する
      context.emit('updateExportPercentage', currentPercentage.value / 2 + exportPercentage.value / 2);
    }

    /**
     * ファイルエクスポートボタン押下時の処理
     * @param {object} csvWriter CsvFileWriterのインスタンス
     */
    async function csvExport(csvWriter = null) {
      const isMultiple = data_monitor.value.dataMonitorFunctionSettings.isMultipleDataMonitor();
      const functionName = isActiveTest.value ? 'individual.individual_diagnosis_function_active_test' :
        isMultiple ? 'message.label_menu_multi_data_monitor' : 'individual.individual_diagnosis_function_data_monitor';
      const targetMeasurement = cachedDetails.measures[0].dmat.find(measurement => measurement.measurement_id === measurementId);
      const fileName = getMeasurementFileName(targetMeasurement.name ?? '', currentComponent.$t(functionName), cachedDetails.information.vin ?? '');

      let csvWriterInstance = null;
      try {
        exportPercentage.value = 0;

        if (!isOnlyCsvExport.value) {
          showCsvProgress.value = true;
          // 再生画面からのCSVエクスポートの場合は、ファイル名を指定して新たにCsvFileWriterを作成する
          csvWriterInstance = new CsvFileWriter(fileName);
          await csvWriterInstance.init();
        } else {
          // 親コンポーネントでのCSVエクスポート中は、渡されたCsvFileWriterを使用して、再生画面のプログレスバーは表示しない
          csvWriterInstance = csvWriter;
        }

        const exportCsvParams = {
          csvFileWriter: csvWriterInstance,
          currentComponent,
          dataMonitorComponent: data_monitor,
          projectDetails: cachedDetails,
          measurementId,
          storage,
          errorDialogCallback: () => backSelect(),
          updateExportProgress,
          isDebug: false
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        throw error;
      } finally {
        showCsvProgress.value = false;
        csvWriterInstance && await csvWriterInstance.close();
      }
    }

    /**
     * 計測データ取得処理の停止
     */
    async function stopFetchingMeasurement() {
      if (inPlaying.value) {
        // 直前の再生データ取得中であれば停止する
        await data_monitor.value.stopFetchingMeasurement();
        inPlaying.value = false;
        currentPercentage.value = 0;
      }
    }

    /**
     * 最大化ボタン押下処理
     */
    function maximum() {
      showModal.value = true;
    }

    /**
     * 最小化ボタン押下処理
     */
    function minimum() {
      showModal.value = false;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function modalContentId() {
      return `full-modal-content-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function graphSettingsDestinationId() {
      return `graph-settings-destination-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 子コンポーネントからDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    function measureMountedEvent(isMounted) {
      measureMounted.value = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        showModal.value = false;
      }
    }

    /**
     * デバッグ用CSVエクスポート処理
     */
    async function debugCsvExport() {
      const isDebug = process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true";
      if (!isDebug) {
        return;
      }

      const fileName = 'debugDataMonitor';

      const originT = currentComponent.$t;
      const originTe = currentComponent.$te;

      // 意図的に翻訳できないようにする
      currentComponent.$t = (v) => v;
      currentComponent.$te = () => false;
      new dmUtil.Cache().destroy();

      let csvWriterInstance = null;
      try {
        csvWriterInstance = new CsvFileWriter(fileName);
        await csvWriterInstance.init();

        showCsvProgress.value = true;
        exportPercentage.value = 0;
        const exportCsvParams = {
          csvFileWriter: csvWriterInstance,
          currentComponent,
          dataMonitorComponent: data_monitor,
          projectDetails: cachedDetails,
          measurementId,
          storage,
          errorDialogCallback: () => backSelect(),
          updateExportProgress,
          isDebug: true
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        throw error;
      } finally {
        showCsvProgress.value = false;
        // 戻す
        currentComponent.$t = originT;
        currentComponent.$te = originTe;
        csvWriterInstance && await csvWriterInstance.close();
      }

      const conversionList = data_monitor.value.conversionList;
      dmUtil.jsonFileExport(conversionList, 'debugConversionList.json');

      const didConversionMap = data_monitor.value.didConversionMap;
      dmUtil.jsonFileExport(didConversionMap, 'debugDidConversionMap.json');
    }

    function ableToCsvExport() {
      ableToExport.value = true;
    }

    onBeforeUnmount(async () => {
      new dmUtil.Cache().destroy();
      // 画面遷移前に再生データ取得中であれば停止する
      await stopFetchingMeasurement();
    });

    onBeforeRouteLeave((to, from, next) => {
      showSelect.value = true;
      next();
    });

    return {
      ...mapActions('auth', ['logout']),
      ICON_KEY,
      BUTTON_TYPE,
      data_monitor,
      inPlaying,
      btnDisabled,
      resetZoomBtnDisabled,
      isZoomPanActive,
      highlightItems,
      currentPercentage,
      exportPercentage,
      dataMonitorFunctionSettings,
      recollectionAtInfo,
      atOperatingStatusList,
      isPhase6AtRecollection,
      showModal,
      showCsvProgress,
      measureMounted,
      showSelect,
      isOnlyCsvExport,
      ableToExport,
      createDmtMeasuresList,
      handleConfigureGraph,
      ...createZoomTabHandlersForRefs(data_monitor, currentTab, () => resetZoomBtnDisabled.value),
      parentOpenMemoModal,
      togglePlaying,
      changeCheckValue,
      zoomPanStateChanged,
      backSelect,
      csvExport,
      updateTotalCount,
      stopFetchingMeasurement,
      maximum,
      minimum,
      modalContentId,
      graphSettingsDestinationId,
      measureMountedEvent,
      debugCsvExport,
      ableToCsvExport
    };
  }
};